import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Eye, EyeOff, ArrowRight, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
`;

const Back = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 16px;
`;

const Card = styled.div`
  background: white;
  padding: 32px 24px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  max-width: 460px;
  margin: 32px auto 0;
`;

const Title = styled.h2`
  margin: 24px 0 0;
  text-align: center;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
  display: block;
  margin-bottom: 6px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.body};
  margin-bottom: 18px;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  margin-top: 3px;
`;

export default function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signup, isAuthenticated, loading: isLoading, error: authError, clearError } = useAuth();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      toast({ title: t("auth.accountCreated"), description: t("auth.welcomeToStoreHub") });
      navigate("/get-started", { replace: true });
    }
  }, [isAuthenticated, navigate, toast]);

  useEffect(() => {
    if (authError) toast({ title: t("auth.signupFailed"), description: authError, variant: "destructive" });
  }, [authError, toast]);

  if (isAuthenticated) return null;

  function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: t("auth.passwordsDontMatch"), description: t("auth.passwordsMatchHint"), variant: "destructive" });
      return;
    }
    if (!terms) {
      toast({ title: t("auth.acceptTerms"), description: t("auth.mustAgreeTerms"), variant: "destructive" });
      return;
    }
    clearError();
    signup({ fullName, email, password });
  }

  const error = authError;

  return (
    <Wrap>
      <Back>
        <Button
          $variant="ghost"
          $size="sm"
          onClick={() => navigate("/")}
        >
          <ChevronLeft size={16} />
          {t("auth.backToHome")}
        </Button>
      </Back>

      <Center>
        <div style={{ maxWidth: 460, margin: "0 auto" }}>
          <Title>{t("auth.createYourAccount")}</Title>
          <Sub>
            {t("auth.alreadyHaveAccount")}{" "}
            <Link
              to="/login"
              style={{
                color: "#9b87f5",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {t("auth.signIn")}
            </Link>
          </Sub>
        </div>

        <Card>
          <form onSubmit={handleSubmit}>
            {error && <p style={{ color: "#dc2626", fontSize: 14, marginBottom: 16 }}>{error}</p>}
            <Field>
              <Label>{t("auth.fullName")}</Label>
              <Input
                placeholder={t("auth.fullNamePlaceholder")}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </Field>

            <Field>
              <Label>{t("auth.emailAddress")}</Label>
              <Input
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>

            <Field>
              <Label>{t("auth.password")}</Label>
              <div style={{ position: "relative" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: 36 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{
                    position: "absolute",
                    insetBlock: 0,
                    right: 10,
                    display: "flex",
                    alignItems: "center",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  {showPassword ? (
                    <EyeOff size={16} color="#9ca3af" />
                  ) : (
                    <Eye size={16} color="#9ca3af" />
                  )}
                </button>
              </div>
            </Field>

            <Field>
              <Label>{t("auth.confirmPassword")}</Label>
              <div style={{ position: "relative" }}>
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{ paddingRight: 36 }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  style={{
                    position: "absolute",
                    insetBlock: 0,
                    right: 10,
                    display: "flex",
                    alignItems: "center",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={16} color="#9ca3af" />
                  ) : (
                    <Eye size={16} color="#9ca3af" />
                  )}
                </button>
              </div>
            </Field>

            <CheckboxRow>
              <Checkbox
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
              />
              <span>
                {t("auth.iAgreeTo")}{" "}
                <a
                  href="#"
                  style={{
                    color: "#9b87f5",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  {t("auth.termsOfService")}
                </a>{" "}
                {t("auth.and")}{" "}
                <a
                  href="#"
                  style={{
                    color: "#9b87f5",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  {t("auth.privacyPolicy")}
                </a>
                .
              </span>
            </CheckboxRow>

            <Button
              type="submit"
              disabled={isLoading}
              style={{ width: "100%", marginTop: 4 }}
            >
              {isLoading ? t("auth.creatingAccount") : t("auth.createAccount")}
              <ArrowRight size={16} />
            </Button>
          </form>
        </Card>
      </Center>
    </Wrap>
  );
}

